<script setup>
// Create / edit an appointment: customer (search or quick create), practitioner → service,
// date + free time slots (or a typed time), partner, price, location, notes.
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PhoneField from 'dashboard/wasapi/common/PhoneField.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { appointmentsAPI, availability, customersAPI } from '../api';
import { STATUSES, fromInputs, toDateInput, toTimeInput } from '../helpers';

const props = defineProps({
  practitioners: { type: Array, default: () => [] },
  services: { type: Array, default: () => [] },
  partners: { type: Array, default: () => [] },
});
const emit = defineEmits(['saved']);
const { t } = useI18n();

const dialogRef = ref(null);
const editingId = ref(null);
const isSaving = ref(false);
const slots = ref([]);
const loadingSlots = ref(false);
const customerQuery = ref('');
const customerResults = ref([]);
const customerLabel = ref('');
const showNewCustomer = ref(false);
const newCustomer = reactive({ name: '', phone: '' });

const blank = () => ({
  customer_id: null,
  practitioner_id: '',
  service_id: '',
  partner_id: '',
  date: '',
  time: '',
  price: '',
  location: '',
  notes: '',
  status: 'scheduled',
  conversation_id: null,
});
const form = reactive(blank());

const activePractitioners = computed(() =>
  props.practitioners.filter(p => p.is_active !== false)
);
// Practitioner without explicit services = no restriction.
const servicesForPractitioner = computed(() => {
  const practitioner = props.practitioners.find(
    p => p.id === Number(form.practitioner_id)
  );
  const all = props.services.filter(s => s.is_active !== false);
  if (!practitioner || !(practitioner.service_ids || []).length) return all;
  return all.filter(s => practitioner.service_ids.includes(s.id));
});
const toOptions = (list, labelKey = 'name') =>
  list.map(item => ({ value: item.id, label: item[labelKey] }));
const statusOptions = computed(() =>
  STATUSES.map(s => ({ value: s, label: t(`WASAPI_APPOINTMENTS.STATUS.${s}`) }))
);
const partnerOptions = computed(() => [
  { value: '', label: t('WASAPI_APPOINTMENTS.FORM.NONE') },
  ...toOptions(props.partners),
]);
const canSave = computed(
  () =>
    form.customer_id &&
    form.practitioner_id &&
    form.service_id &&
    form.date &&
    form.time
);

const loadSlots = async () => {
  if (!form.practitioner_id || !form.service_id || !form.date) {
    slots.value = [];
    return;
  }
  loadingSlots.value = true;
  try {
    const { data } = await availability({
      practitioner_id: form.practitioner_id,
      service_id: form.service_id,
      date: form.date,
    });
    slots.value = data.payload || [];
  } catch (error) {
    slots.value = [];
  } finally {
    loadingSlots.value = false;
  }
};
watch(() => [form.practitioner_id, form.service_id, form.date], loadSlots);
watch(
  () => form.practitioner_id,
  () => {
    if (
      !servicesForPractitioner.value.some(s => s.id === Number(form.service_id))
    )
      form.service_id = '';
  }
);
watch(
  () => form.service_id,
  id => {
    const service = props.services.find(s => s.id === Number(id));
    if (service?.default_price && !form.price)
      form.price = service.default_price;
  }
);

let searchTimer = null;
const searchCustomers = () => {
  clearTimeout(searchTimer);
  if (customerQuery.value.trim().length < 2) {
    customerResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    const { data } = await customersAPI.list({ q: customerQuery.value.trim() });
    customerResults.value = data.payload || [];
  }, 250);
};
const pickCustomer = customer => {
  form.customer_id = customer.id;
  customerLabel.value = `${customer.name} · ${customer.phone}`;
  customerResults.value = [];
  customerQuery.value = '';
};
const clearCustomer = () => {
  form.customer_id = null;
  customerLabel.value = '';
};
const createCustomer = async () => {
  if (!newCustomer.name.trim() || !newCustomer.phone.trim()) return;
  try {
    const { data } = await customersAPI.create({
      name: newCustomer.name.trim(),
      phone: newCustomer.phone.trim(),
    });
    pickCustomer(data);
    showNewCustomer.value = false;
    newCustomer.name = '';
    newCustomer.phone = '';
  } catch (error) {
    useAlert(t('WASAPI_APPOINTMENTS.COMMON.SAVE_ERROR'));
  }
};

const open = ({ appointment = null, prefill = {} } = {}) => {
  Object.assign(form, blank(), prefill);
  editingId.value = appointment?.id || null;
  customerLabel.value = '';
  slots.value = [];
  showNewCustomer.value = false;
  if (appointment) {
    const at = new Date(appointment.appointment_at);
    Object.assign(form, {
      customer_id: appointment.customer_id,
      practitioner_id: appointment.practitioner_id,
      service_id: appointment.service_id,
      partner_id: appointment.partner_id || '',
      date: toDateInput(at),
      time: toTimeInput(at),
      price: appointment.price ?? '',
      location: appointment.location || '',
      notes: appointment.notes || '',
      status: appointment.status,
      conversation_id: appointment.conversation_id,
    });
    customerLabel.value = `${appointment.customer_name} · ${appointment.customer_phone || ''}`;
  } else if (prefill.customer) {
    pickCustomer(prefill.customer);
  }
  dialogRef.value?.open();
};

const save = async () => {
  if (!canSave.value) {
    useAlert(t('WASAPI_APPOINTMENTS.FORM.REQUIRED'));
    return;
  }
  isSaving.value = true;
  const payload = {
    customer_id: form.customer_id,
    practitioner_id: form.practitioner_id,
    service_id: form.service_id,
    partner_id: form.partner_id || null,
    appointment_at: fromInputs(form.date, form.time).toISOString(),
    price: form.price === '' ? null : form.price,
    location: form.location,
    notes: form.notes,
    status: form.status,
    conversation_id: form.conversation_id,
  };
  try {
    const { data } = editingId.value
      ? await appointmentsAPI.update(editingId.value, payload)
      : await appointmentsAPI.create(payload);
    useAlert(t('WASAPI_APPOINTMENTS.FORM.SAVED'));
    emit('saved', data);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WASAPI_APPOINTMENTS.COMMON.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      editingId
        ? t('WASAPI_APPOINTMENTS.FORM.EDIT_TITLE')
        : t('WASAPI_APPOINTMENTS.FORM.NEW_TITLE')
    "
    :confirm-button-label="t('WASAPI_APPOINTMENTS.COMMON.SAVE')"
    :is-loading="isSaving"
    :disable-confirm-button="!canSave"
    width="xl"
    overflow-y-auto
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <!-- customer -->
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_APPOINTMENTS.FORM.CUSTOMER') }}
        </span>
        <div
          v-if="form.customer_id"
          class="flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12"
        >
          <span class="truncate">{{ customerLabel }}</span>
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-x"
            @click="clearCustomer"
          />
        </div>
        <template v-else>
          <div class="relative">
            <Input
              v-model="customerQuery"
              :placeholder="t('WASAPI_APPOINTMENTS.FORM.CUSTOMER_SEARCH')"
              @input="searchCustomers"
            />
            <ul
              v-if="customerResults.length"
              class="absolute left-0 right-0 z-10 mt-1 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-1 border-n-weak max-h-48"
            >
              <li
                v-for="customer in customerResults"
                :key="customer.id"
                class="px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-1 text-n-slate-12"
                @click="pickCustomer(customer)"
              >
                {{ customer.name }}
                <span class="text-n-slate-10">· {{ customer.phone }}</span>
              </li>
            </ul>
          </div>
          <button
            type="button"
            class="self-start text-xs text-n-blue-11 hover:underline"
            @click="showNewCustomer = !showNewCustomer"
          >
            {{ t('WASAPI_APPOINTMENTS.FORM.CUSTOMER_NEW') }}
          </button>
          <div
            v-if="showNewCustomer"
            class="grid grid-cols-1 gap-2 p-3 rounded-lg md:grid-cols-3 bg-n-alpha-1"
          >
            <Input
              v-model="newCustomer.name"
              :placeholder="t('WASAPI_APPOINTMENTS.COMMON.NAME')"
            />
            <PhoneField
              v-model="newCustomer.phone"
              :placeholder="t('WASAPI_APPOINTMENTS.COMMON.PHONE')"
            />
            <Button
              :label="t('WASAPI_APPOINTMENTS.FORM.CUSTOMER_CREATE')"
              color="slate"
              variant="faded"
              size="sm"
              type="button"
              @click="createCustomer"
            />
          </div>
        </template>
      </div>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('WASAPI_APPOINTMENTS.FORM.PRACTITIONER') }}
          <ComboBox
            v-model="form.practitioner_id"
            :options="toOptions(activePractitioners)"
            :placeholder="t('WASAPI_APPOINTMENTS.FORM.SELECT')"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('WASAPI_APPOINTMENTS.FORM.SERVICE') }}
          <ComboBox
            v-model="form.service_id"
            :options="toOptions(servicesForPractitioner)"
            :placeholder="t('WASAPI_APPOINTMENTS.FORM.SELECT')"
            class="w-full"
          />
        </div>
        <Input
          v-model="form.date"
          type="date"
          :label="t('WASAPI_APPOINTMENTS.FORM.DATE')"
        />
        <Input
          v-model="form.time"
          type="time"
          :label="t('WASAPI_APPOINTMENTS.FORM.TIME')"
        />
      </div>

      <div v-if="form.practitioner_id && form.service_id && form.date">
        <span class="text-xs text-n-slate-11">
          {{ t('WASAPI_APPOINTMENTS.FORM.AVAILABLE_TIMES') }}
        </span>
        <p v-if="loadingSlots" class="mt-1 text-xs text-n-slate-10">
          {{ t('WASAPI_APPOINTMENTS.COMMON.LOADING') }}
        </p>
        <p v-else-if="!slots.length" class="mt-1 text-xs text-n-slate-10">
          {{ t('WASAPI_APPOINTMENTS.FORM.NO_SLOTS') }}
        </p>
        <div v-else class="flex flex-wrap gap-1.5 mt-1">
          <button
            v-for="slot in slots"
            :key="slot.iso"
            type="button"
            class="px-2.5 py-1 text-xs rounded-md border"
            :class="
              form.time === slot.time
                ? 'bg-n-blue-9 text-white border-n-blue-9'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="form.time = slot.time"
          >
            {{ slot.time }}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('WASAPI_APPOINTMENTS.FORM.PARTNER') }}
          <ComboBox
            v-model="form.partner_id"
            :options="partnerOptions"
            class="w-full"
          />
        </div>
        <Input
          v-model="form.price"
          type="number"
          :label="t('WASAPI_APPOINTMENTS.FORM.PRICE')"
        />
        <Input
          v-model="form.location"
          :label="t('WASAPI_APPOINTMENTS.FORM.LOCATION')"
        />
      </div>
      <div v-if="editingId" class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('WASAPI_APPOINTMENTS.FORM.STATUS') }}
        <ComboBox
          v-model="form.status"
          :options="statusOptions"
          class="w-full"
        />
      </div>
      <TextArea
        v-model="form.notes"
        :label="t('WASAPI_APPOINTMENTS.FORM.NOTES')"
        :max-length="2000"
      />
    </div>
  </Dialog>
</template>
